import { formatMoney, getCurrencyPrecision } from '@theme/money-formatting';
import { cartService } from '@theme/tk-cart';

/* tk-pdp.js — controller for sections/tk-pdp-bouquet.liquid (Urbanstems-style
   bouquet PDP). One custom element wraps the whole section:

   <tk-pdp-root>
     • variant cards (role=radio) — toggles every pre-rendered per-variant node
       ([data-tk-vprice], [data-tk-vpill], [data-tk-vpanel]), re-prices the
       ADD TO CART button and mirrors the choice into the URL (?variant=).
     • upsell drawers — PENDING-SELECTION model (like the reference): ADD in
       the drawer only SELECTS the product (card gets a corner check, button
       flips to Remove, an "ADD SELECTED — $x" footer appears; nothing touches
       the cart). Closing the drawer summarizes the pick in its card row
       (check on the thumb, product name + price, link becomes "CHANGE") and
       the ADD TO CART price becomes bouquet + every selected extra. One
       selection per row; the row's variant <select> retargets it live.
     • ADD TO CART — passes the bouquet variant PLUS all selected extras to the
       shared Threadkins cart service. That service owns the page-wide queue,
       locale-safe routes, exact commit proof, and Horizon cart event. Pending
       selections clear only after the service confirms a committed add.
     • media pane carousel — prev/next circles scroll by one natural-width
       pane; disabled states sync on scroll (no rAF anywhere).
     • "what's inside" tiles — flower preview <dialog> + mobile collapse.

   Also drives sections/tk-pdp-flower.liquid (same <tk-pdp-root>): its colour
   swatches are the same data-tk-variant radios, and selectVariant additionally
   paints the picked colour's name ([data-tk-cname]) and swaps the hero pane
   photo ([data-tk-hero], stash/restore so an imageless variant falls back to
   the original). Both hooks are absent on bouquet pages and no-op there.
   Safe across theme-editor re-renders (AbortController re-bind). */
(() => {
  'use strict';
  if (customElements.get('tk-pdp-root')) return;

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollerOf = (el) => {
    let n = el.parentElement;
    while (n && n !== document.documentElement) {
      const c = getComputedStyle(n);
      if (/(auto|scroll)/.test(c.overflowY) && n.scrollHeight > n.clientHeight) return n;
      n = n.parentElement;
    }
    return document.querySelector('.page-wrapper') || document.documentElement;
  };

  class TkPdpRoot extends HTMLElement {
    connectedCallback() {
      if (this.ac) this.ac.abort();
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };

      this.vdata = {};
      try {
        const raw = this.querySelector('[data-tk-vdata]');
        if (raw) this.vdata = JSON.parse(raw.textContent);
      } catch (e) { this.vdata = {}; }

      // pending upsell selections: blockId -> {vid, cents, title, variantTitle}
      this.upsells = new Map();

      // flower-PDP hooks (absent on bouquet pages)
      this.cname = this.querySelector('[data-tk-cname]');
      this.hero = this.querySelector('[data-tk-hero]');

      // ----- variant cards
      this.cards = [...this.querySelectorAll('[data-tk-variant]')];
      const initInfo = this.variantInfo(this.atbVariantId()) || (this.vdata.variants || [])[0];
      this.baseCents = initInfo ? initInfo.cents : 0;
      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.selectVariant(card.dataset.tkVariant), o);
        card.addEventListener('keydown', (e) => {
          const i = this.cards.indexOf(card);
          let n = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % this.cards.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + this.cards.length) % this.cards.length;
          if (n === null) return;
          e.preventDefault();
          this.selectVariant(this.cards[n].dataset.tkVariant);
          this.cards[n].focus();
        }, o);
      });

      // ----- add to bag (bouquet + selected extras)
      this.atb = this.querySelector('[data-tk-atb]');
      // preventDefault is REQUIRED now that the button is a native form submit
      // (F81 no-JS fallback): without it, a JS-enabled click both runs addMain
      // AND submits the form → double add + navigate to /cart.
      this.atb && this.atb.addEventListener('click', (e) => { e.preventDefault(); this.addMain(); }, o);

      // ----- sticky add-to-cart bar ([data-tk-sticky] only renders on bouquet pages)
      this.initStickyAtb(o);

      // ----- media pane carousel
      const mt = this.querySelector('[data-tk-mediatrack]');
      const mp = this.querySelector('[data-tk-mprev]');
      const mn = this.querySelector('[data-tk-mnext]');
      if (mt) {
        const paneW = () => {
          const c = mt.firstElementChild;
          const gap = parseFloat(getComputedStyle(mt).columnGap) || 0;
          return c ? c.getBoundingClientRect().width + gap : mt.clientWidth / 2;
        };
        const sync = () => {
          const max = mt.scrollWidth - mt.clientWidth;
          if (mp) mp.toggleAttribute('disabled', mt.scrollLeft < 4);
          if (mn) mn.toggleAttribute('disabled', mt.scrollLeft > max - 4);
        };
        mp && mp.addEventListener('click', () => mt.scrollBy({ left: -paneW(), behavior: reduced() ? 'auto' : 'smooth' }), o);
        mn && mn.addEventListener('click', () => mt.scrollBy({ left: paneW(), behavior: reduced() ? 'auto' : 'smooth' }), o);

        // mobile dots + "See more": dots mirror the swipe position, tapping a
        // dot jumps to that photo, "See more" advances one photo
        const dots = [...this.querySelectorAll('[data-tk-mdot]')];
        const see = this.querySelector('[data-tk-msee]');
        const paneIndex = () => Math.min(Math.round(mt.scrollLeft / paneW()), Math.max(dots.length - 1, 0));
        const dotSync = () => {
          if (!dots.length) return;
          const i = paneIndex();
          dots.forEach((d, j) => d.classList.toggle('is-active', j === i));
        };
        dots.forEach((d) => d.addEventListener('click', () =>
          mt.scrollTo({ left: Number(d.dataset.tkMdot) * paneW(), behavior: reduced() ? 'auto' : 'smooth' }), o));
        see && see.addEventListener('click', () => {
          const n = Math.min(paneIndex() + 1, dots.length - 1);
          mt.scrollTo({ left: n * paneW(), behavior: reduced() ? 'auto' : 'smooth' });
        }, o);

        const syncAll = () => { sync(); dotSync(); };
        mt.addEventListener('scroll', syncAll, o);
        addEventListener('resize', syncAll, o);
        syncAll();
      }

      // ----- "what's inside" rows: intercept the real Learn-more link into the
      // in-page peek dialog (the href stays crawlable / works without JS)
      this.addEventListener('click', (e) => {
        const tile = e.target.closest && e.target.closest('[data-tk-tile]');
        if (tile) { e.preventDefault(); this.openFlowerPop(tile); return; }
        const px = e.target.closest && e.target.closest('[data-tk-pop-close]');
        if (px) {
          const pop = px.closest('dialog');
          if (pop && pop.close) pop.close();
        }
      }, o);

      // ----- upsell drawers (pending-selection model)
      this.querySelectorAll('[data-tk-drawer-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openDrawer(btn.dataset.tkDrawerOpen, btn), o);
      });
      this.querySelectorAll('dialog[data-tk-drawer]').forEach((dlg) => {
        dlg.addEventListener('click', (e) => { if (e.target === dlg) this.closeDrawer(dlg); }, o);
        dlg.addEventListener('cancel', (e) => { e.preventDefault(); this.closeDrawer(dlg); }, o);
        dlg.querySelectorAll('[data-tk-drawer-close]').forEach((x) =>
          x.addEventListener('click', () => this.closeDrawer(dlg), o)
        );
        const blockId = dlg.dataset.blockId;
        dlg.querySelectorAll('[data-tk-ucard]').forEach((row) => {
          const sel = row.querySelector('[data-tk-uvariant]');
          const add = row.querySelector('[data-tk-uadd]');
          const priceOut = row.querySelector('[data-tk-uprice]');
          if (sel) {
            sel.addEventListener('change', () => {
              const opt = sel.options[sel.selectedIndex];
              if (add) {
                add.dataset.vid = sel.value;
                add.dataset.cents = opt.dataset.cents || '0';
                add.disabled = opt.disabled;
              }
              if (priceOut && opt.dataset.cents) priceOut.textContent = this.fmt(Number(opt.dataset.cents));
              // thumbnail follows the picked variant (stash/restore for
              // variants without their own image)
              const uimg = row.querySelector('.tk-pdpB__uimg img');
              if (uimg) {
                if (uimg.dataset.tkOrigSrc === undefined) {
                  uimg.dataset.tkOrigSrc = uimg.getAttribute('src') || '';
                  uimg.dataset.tkOrigSrcset = uimg.getAttribute('srcset') || '';
                }
                const nsrc = opt.dataset.imgSrc || uimg.dataset.tkOrigSrc;
                if (nsrc) {
                  if (opt.dataset.imgSrc) uimg.removeAttribute('srcset');
                  else if (uimg.dataset.tkOrigSrcset) uimg.setAttribute('srcset', uimg.dataset.tkOrigSrcset);
                  uimg.src = nsrc;
                }
              }
              // if this card is the current selection, retarget it live — but
              // if the newly picked combo is sold out, CLEAR it instead of
              // retargeting (F01/F79): retargeting would store a sold-out vid,
              // freeze Remove, and make the whole ADD TO CART 422.
              const cur = this.upsells.get(blockId);
              if (cur && row.classList.contains('is-selected')) {
                if (add && add.disabled) this.clearUpsell(blockId, dlg);
                else this.setUpsell(blockId, dlg, row, add);
              } else {
                this.refreshUaddLabel(row, add);
              }
            }, o);
          }
          if (add) {
            add.addEventListener('click', () => {
              if (add.disabled) return;
              if (row.classList.contains('is-selected')) this.clearUpsell(blockId, dlg);
              else this.setUpsell(blockId, dlg, row, add);
            }, o);
          }

          // option pills / colour swatches — proxies for the hidden select:
          // resolve the value combo to a variant, sync the select (its change
          // handler re-prices and retargets), and treat picking an option on
          // an unselected tile as selecting it
          row.querySelectorAll('[data-tk-uopt]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const groups = [...row.querySelectorAll('[data-tk-uoptgroup]')];
              const group = btn.closest('[data-tk-uoptgroup]');
              const clickedIdx = groups.indexOf(group);
              if (group) {
                group.querySelectorAll('[data-tk-uopt]').forEach((b) => {
                  const on = b === btn;
                  b.classList.toggle('is-selected', on);
                  b.setAttribute('aria-checked', on ? 'true' : 'false');
                  b.tabIndex = on ? 0 : -1;
                });
              }
              if (sel) {
                const opts = [...sel.options];
                // match on exact per-option attributes (data-opt1..3), NOT a
                // ' / '-joined title — a value containing ' / ' would break the join.
                const key = (op, i) => op.dataset['opt' + (i + 1)];
                const vals = groups.map((g) => {
                  const on = g.querySelector('[data-tk-uopt].is-selected');
                  return on ? on.dataset.value : null;
                });
                let opt = opts.find((op) => groups.every((g, gi) => key(op, gi) === vals[gi]));
                if (!opt) {
                  // sparse matrix: no variant for this combo. Snap to the first
                  // (preferably available) variant that honours the CLICKED value,
                  // then reflect that valid combo back into every pill group so
                  // the UI and the hidden select never disagree (F02).
                  const cands = opts.filter((op) => key(op, clickedIdx) === btn.dataset.value);
                  opt = cands.find((op) => !op.disabled) || cands[0];
                  if (opt) {
                    groups.forEach((g, gi) => {
                      const want = key(opt, gi);
                      g.querySelectorAll('[data-tk-uopt]').forEach((b) => {
                        const on = b.dataset.value === want;
                        b.classList.toggle('is-selected', on);
                        b.setAttribute('aria-checked', on ? 'true' : 'false');
                        b.tabIndex = on ? 0 : -1;
                      });
                    });
                  }
                }
                if (opt) {
                  sel.value = opt.value;
                  sel.dispatchEvent(new Event('change'));
                }
              }
              if (!row.classList.contains('is-selected') && add && !add.disabled) {
                this.setUpsell(blockId, dlg, row, add);
              }
            }, o);
            btn.addEventListener('keydown', (e) => {
              const group = btn.closest('[data-tk-uoptgroup]');
              if (!group) return;
              const opts = [...group.querySelectorAll('[data-tk-uopt]')];
              const i = opts.indexOf(btn);
              let n = null;
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % opts.length;
              else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + opts.length) % opts.length;
              else if (e.key === 'Home') n = 0;
              else if (e.key === 'End') n = opts.length - 1;
              if (n === null) return;
              e.preventDefault();
              opts[n].click();
              opts[n].focus();
            }, o);
          });

          // the whole tile selects (US pattern); buttons/links keep their jobs
          row.addEventListener('click', (e) => {
            if (e.target.closest('button, a, select, [data-tk-uopt]')) return;
            if (!row.classList.contains('is-selected') && add && !add.disabled) {
              this.setUpsell(blockId, dlg, row, add);
            }
          }, o);
        });
      });

      this.updateAtb();
    }

    disconnectedCallback() { if (this.ac) this.ac.abort(); }

    // ---------- helpers
    fmt(liquidMoneyValue) {
      const data = this.vdata || {};
      const currency = data.currency || window.Shopify?.currency?.active || 'CAD';
      const format = data.moneyFormat || '{{ amount }}';
      const parsed = Number(liquidMoneyValue);
      const rawValue = Number.isFinite(parsed) ? parsed : 0;

      // Liquid appends two zero digits for currencies without subunits:
      // ¥1,000 is exposed as 100000, while three-decimal currencies retain
      // their native minor units. Normalize only the documented zero-decimal
      // exception, then let Horizon's shared formatter handle every separator.
      const precision = getCurrencyPrecision(currency);
      const minorUnits = precision === 0 ? rawValue / 100 : rawValue;
      const out = formatMoney(minorUnits, format, currency);
      // threadkins integer pricing: drop the cents ONLY on exact whole amounts
      // (mirrors Liquid's money_without_trailing_zeros); non-round values keep cents.
      if (precision > 0 && Math.round(rawValue) % Math.pow(10, precision) === 0) {
        return out.replace(/([.,])0{2}(?=\D*$)/, '');
      }
      return out;
    }

    variantInfo(id) {
      const list = (this.vdata && this.vdata.variants) || [];
      return list.find((v) => String(v.id) === String(id));
    }

    atbVariantId() { return this.atb ? this.atb.dataset.variantId : null; }

    upsellTotal() {
      let t = 0;
      this.upsells.forEach((u) => { t += u.cents; });
      return t;
    }

    updateAtb() {
      if (!this.atb) return;
      const info = this.variantInfo(this.atbVariantId());
      const label = this.atb.querySelector('[data-tk-atb-label]');
      if (!label) return;
      if (info && !info.available) {
        label.textContent = (this.vdata && this.vdata.soldout) || 'Sold out';
        return;
      }
      const base = info ? info.cents : this.baseCents;
      label.textContent = ((this.vdata && this.vdata.atbLabel) || 'ADD TO CART') + ' — ' + this.fmt(base + this.upsellTotal());
    }

    // ---------- variants
    selectVariant(id) {
      const info = this.variantInfo(id);
      this.cards.forEach((c) => {
        const on = String(c.dataset.tkVariant) === String(id);
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
        c.tabIndex = on ? 0 : -1;
      });
      this.querySelectorAll('[data-tk-vprice], [data-tk-vpill], [data-tk-vpanel]').forEach((n) => {
        const key = n.dataset.tkVprice || n.dataset.tkVpill || n.dataset.tkVpanel;
        n.hidden = String(key) !== String(id);
      });
      if (this.atb && info) {
        this.atb.dataset.variantId = id;
        // keep the native form fallback (F81) pointed at the selected variant
        const idInput = this.atb.form && this.atb.form.querySelector('input[name="id"]');
        if (idInput) idInput.value = id;
        this.atb.disabled = !info.available;
      }
      this.updateAtb();
      try {
        const url = new URL(location.href);
        url.searchParams.set('variant', id);
        history.replaceState({}, '', url);
      } catch (e) { /* noop */ }
      const card = this.cards.find((c) => String(c.dataset.tkVariant) === String(id));
      if (card) {
        if (this.cname && card.dataset.name) this.cname.textContent = card.dataset.name;
        if (this.hero) this.swapHero(card);
      }
      // threadkins custom (completion-rail phase 1): announce the pick so
      // sections/tk-flr-complete.liquid can re-curate. Additive only — fires
      // after every existing statement; nothing else in the theme listens.
      try {
        document.dispatchEvent(new CustomEvent('tk:variant-change', {
          detail: { id: String(id), available: Boolean(info && info.available) },
        }));
      } catch (e) { /* noop */ }
    }

    // flower PDP: paint the hero pane with the selected colour's photo.
    // A variant without an image (e.g. Leaves/Orange) restores the original;
    // the dim-while-loading mirrors the collection cards (tk-flcard-info).
    swapHero(card) {
      const img = this.hero;
      if (img.dataset.tkOrigSrc === undefined) {
        img.dataset.tkOrigSrc = img.getAttribute('src') || '';
        img.dataset.tkOrigSrcset = img.getAttribute('srcset') || '';
        img.dataset.tkOrigAlt = img.getAttribute('alt') || '';
      }
      const hasVariantImage = Boolean(card.dataset.imgSrc);
      const src = hasVariantImage ? card.dataset.imgSrc : img.dataset.tkOrigSrc;
      const srcset = hasVariantImage ? (card.dataset.imgSrcset || '') : img.dataset.tkOrigSrcset;
      if (!src) return;
      const done = () => {
        img.style.opacity = '';
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
      };
      img.style.opacity = '.35';
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      if (srcset) img.setAttribute('srcset', srcset);
      else img.removeAttribute('srcset');
      img.alt = hasVariantImage ? (card.dataset.imgAlt || img.dataset.tkOrigAlt) : img.dataset.tkOrigAlt;
      img.src = src;
      if (img.complete) done();
      // if the shopper had swiped to a group photo, bring the hero back
      const mt = this.querySelector('[data-tk-mediatrack]');
      if (mt && mt.scrollLeft > 0) mt.scrollTo({ left: 0, behavior: reduced() ? 'auto' : 'smooth' });
    }

    // ---------- upsell selection state
    setUpsell(blockId, dlg, row, add) {
      // defense-in-depth (F79): never store a sold-out variant id, whatever the caller
      if (add && add.disabled) { this.clearUpsell(blockId, dlg); return; }
      // radio behaviour: one selection per drawer
      dlg.querySelectorAll('[data-tk-ucard]').forEach((r) => {
        r.classList.toggle('is-selected', r === row);
        const a = r.querySelector('[data-tk-uadd]');
        this.refreshUaddLabel(r, a);
      });
      const sel = row.querySelector('[data-tk-uvariant]');
      const opt = sel ? sel.options[sel.selectedIndex] : null;
      this.upsells.set(blockId, {
        vid: Number(add.dataset.vid),
        cents: Number(add.dataset.cents || 0),
        title: add.dataset.title || '',
        variantTitle: opt && sel.options.length > 1 ? opt.text.replace(/\s+—\s+sold out$/i, '') : '',
      });
      row.classList.add('is-selected');
      this.refreshUaddLabel(row, add);
      this.syncDrawerFoot(dlg);
      this.syncRow(blockId);
      this.updateAtb();
    }

    clearUpsell(blockId, dlg) {
      this.upsells.delete(blockId);
      if (dlg) {
        dlg.querySelectorAll('[data-tk-ucard]').forEach((r) => {
          r.classList.remove('is-selected');
          this.refreshUaddLabel(r, r.querySelector('[data-tk-uadd]'));
        });
        this.syncDrawerFoot(dlg);
      }
      this.syncRow(blockId);
      this.updateAtb();
    }

    refreshUaddLabel(row, add) {
      if (!add) return;
      const label = add.querySelector('[data-tk-uadd-label]');
      if (!label) return;
      if (row.classList.contains('is-selected')) {
        label.textContent = (this.vdata && this.vdata.removeLabel) || 'Remove';
      } else {
        // price lives in the card's title row + drawer footer — the button stays clean
        label.textContent = (this.vdata && this.vdata.addLabel) || 'Add';
      }
    }

    syncDrawerFoot(dlg) {
      const foot = dlg.querySelector('[data-tk-dfoot]');
      if (!foot) return;
      const sel = this.upsells.get(dlg.dataset.blockId);
      foot.hidden = !sel;
      const p = foot.querySelector('[data-tk-dfoot-price]');
      if (sel && p) p.textContent = this.fmt(sel.cents);
    }

    syncRow(blockId) {
      const row = this.querySelector('[data-tk-urow="' + blockId + '"]');
      if (!row) return;
      const title = row.querySelector('[data-tk-urow-title]');
      const sub = row.querySelector('[data-tk-urow-sub]');
      const link = row.querySelector('[data-tk-urow-link]');
      const check = row.querySelector('[data-tk-urow-check]');
      if (title && title.dataset.orig === undefined) title.dataset.orig = title.textContent;
      if (sub && sub.dataset.orig === undefined) sub.dataset.orig = sub.textContent;
      if (link && link.dataset.orig === undefined) link.dataset.orig = link.textContent;
      const sel = this.upsells.get(blockId);
      if (sel) {
        if (title) title.textContent = sel.title;
        if (sub) sub.textContent = this.fmt(sel.cents) + (sel.variantTitle ? ' · ' + sel.variantTitle : '');
        if (link) link.textContent = (this.vdata && this.vdata.changeLabel) || 'Change';
        if (check) check.hidden = false;
      } else {
        if (title) title.textContent = title.dataset.orig;
        if (sub) sub.textContent = sub.dataset.orig;
        if (link) link.textContent = link.dataset.orig;
        if (check) check.hidden = true;
      }
    }

    // ---------- add to bag
    // ---------- sticky add-to-cart bar
    // The bar never owns state: its button routes through the same addMain()
    // (flash() re-entry guard included), and a MutationObserver copies the real
    // button's label + classes, so upsell pricing, Added ✓, Sold out and busy
    // states all stay in sync for free.
    initStickyAtb(o) {
      const bar = this.querySelector('[data-tk-sticky]');
      if (!bar || !this.atb) return;
      const btn = bar.querySelector('[data-tk-sticky-atb]');
      if (!btn) return;
      const label = bar.querySelector('[data-tk-sticky-label]');
      const mainLabel = this.atb.querySelector('[data-tk-atb-label]');
      btn.addEventListener('click', () => this.addMain(), o);
      const sync = () => {
        if (label && mainLabel) label.textContent = mainLabel.textContent;
        btn.disabled = this.atb.disabled;
        if (this.atb.getAttribute('aria-disabled') === 'true') btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
        btn.classList.toggle('is-busy', this.atb.classList.contains('is-busy'));
        btn.classList.toggle('is-added', this.atb.classList.contains('is-added'));
      };
      sync();
      const mo = new MutationObserver(sync);
      mo.observe(this.atb, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled'], childList: true, characterData: true, subtree: true });
      // show only once the real button has scrolled up past the top of the
      // viewport — out-of-view BELOW (page load) keeps the bar hidden and the
      // first paint clean. A capture-phase scroll listener (not an
      // IntersectionObserver) because IO never fires when a momentum fling or
      // anchor jump skips the button's brief in-viewport state, and because it
      // hears Horizon's inner .page-wrapper scroller, whose events don't bubble.
      let stickyRaf = 0;
      const updateStuck = () => {
        const next = this.atb.getBoundingClientRect().bottom <= 0 ? 'true' : 'false';
        if (bar.dataset.stuck !== next) bar.dataset.stuck = next;
      };
      const queueStuck = () => {
        if (stickyRaf) return;
        stickyRaf = requestAnimationFrame(() => { stickyRaf = 0; updateStuck(); });
      };
      document.addEventListener('scroll', queueStuck, { capture: true, passive: true, signal: o.signal });
      addEventListener('resize', queueStuck, o);
      updateStuck();
      o.signal.addEventListener('abort', () => { mo.disconnect(); if (stickyRaf) cancelAnimationFrame(stickyRaf); });
    }

    addMain() {
      if (!this.atb || this.atb.disabled) return;
      const vid = Number(this.atbVariantId());
      if (!vid) return;
      const items = [{ id: vid, quantity: 1 }];
      const committedUpsells = new Map(this.upsells);
      this.upsells.forEach((u) => { if (u.vid) items.push({ id: u.vid, quantity: 1 }); });
      this.flash(this.atb, () => cartService.add(items, {
        source: 'tk-pdp',
        sourceId: this.id || 'tk-pdp',
        context: 'product',
        autoOpen: true,
        dedupeKey: this.atb,
        eventTarget: this,
      }), () => {
        // Clear only the exact selections that were committed. A shopper can
        // change an upsell during the success feedback window; that newer
        // pending choice must survive the older request's cleanup timer.
        committedUpsells.forEach((selection, blockId) => {
          if (this.upsells.get(blockId) !== selection) return;
          const dlg = this.querySelector('dialog[data-block-id="' + blockId + '"]');
          this.clearUpsell(blockId, dlg);
        });
      });
    }

    flash(btn, doAdd, onSuccess) {
      // aria-disabled doesn't block clicks — without this, a double-click
      // queues a second add and the customer gets a duplicate line
      if (btn.classList.contains('is-busy')) return;
      const label = btn.querySelector('[data-tk-atb-label], [data-tk-uadd-label]') || btn;
      const flashVersion = (btn._tkFlashVersion || 0) + 1;
      btn._tkFlashVersion = flashVersion;
      const release = () => {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-busy');
      };
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-busy');
      doAdd()
        .then(() => {
          label.textContent = (this.vdata && this.vdata.added) || 'Added ✓';
          btn.classList.add('is-added');
          setTimeout(() => {
            if (btn._tkFlashVersion !== flashVersion) return;
            btn.classList.remove('is-added');
            try {
              if (onSuccess) onSuccess();
            } finally {
              try { this.updateAtb(); } finally { release(); }
            }
          }, 1600);
        })
        .catch((err) => {
          if (err && err.requiresCartCheck) {
            label.textContent = (this.vdata && this.vdata.checkCart) || 'Check bag';
          } else {
            label.textContent = /sold out|unavailable|out of stock|no longer available/i.test(String(err && err.message))
              ? ((this.vdata && this.vdata.soldout) || 'Sold out')
              : ((this.vdata && this.vdata.error) || 'Try again');
          }
          release();
          setTimeout(() => {
            if (btn._tkFlashVersion !== flashVersion) return;
            this.updateAtb();
          }, 2200);
        });
    }

    // ---------- flower preview
    openFlowerPop(tile) {
      const wrap = tile.closest('.tk-bqp');
      const pop = wrap && wrap.querySelector('[data-tk-bqp-pop]');
      if (!pop) return;
      const img = pop.querySelector('.tk-bqp__pop-img');
      const name = pop.querySelector('.tk-bqp__pop-name');
      const meta = pop.querySelector('.tk-bqp__pop-meta');
      const price = pop.querySelector('.tk-bqp__pop-price');
      const link = pop.querySelector('.tk-bqp__pop-link');
      if (img) {
        img.classList.add('is-loading');
        img.onload = () => img.classList.remove('is-loading');
        img.src = tile.dataset.img || '';
        img.alt = tile.dataset.name || '';
      }
      if (name) name.textContent = tile.dataset.name || '';
      if (meta) {
        const bits = [];
        if (tile.dataset.variant) bits.push(tile.dataset.variant);
        if (Number(tile.dataset.qty) > 1) bits.push(tile.dataset.qty + ' in this bouquet');
        meta.textContent = bits.join(' · ');
      }
      if (price) price.textContent = (tile.dataset.price || '') + ' on its own';
      if (link) link.href = tile.dataset.url || '#';
      if (pop.showModal) pop.showModal();
      else pop.setAttribute('open', '');
    }

    // ---------- drawer open/close
    openDrawer(id, opener) {
      const dlg = this.querySelector('#' + CSS.escape(id));
      if (!dlg) return;
      this.lastFocus = opener || document.activeElement;
      this.lockEl = scrollerOf(this);
      this.prevOverflow = this.lockEl.style.overflow;
      this.prevGutter = this.lockEl.style.scrollbarGutter;
      this.lockEl.style.overflow = 'hidden';
      // keep the hidden scrollbar's width reserved — without it the page
      // reflows ~11px wider while the drawer is open (visible logo shift)
      this.lockEl.style.scrollbarGutter = 'stable';
      if (dlg.showModal) dlg.showModal();
      else dlg.setAttribute('open', '');
    }

    closeDrawer(dlg) {
      if (dlg.open && dlg.close) dlg.close();
      else dlg.removeAttribute('open');
      if (this.lockEl) {
        this.lockEl.style.overflow = this.prevOverflow || '';
        this.lockEl.style.scrollbarGutter = this.prevGutter || '';
      }
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
  }
  customElements.define('tk-pdp-root', TkPdpRoot);
})();
